// frontend/src/components/handbook/DocumentBlocks.js
// Shared structured-content renderer for the Employee Handbook and Company
// Policies. Both surfaces store content as the same ordered "block" model
// (p / h / ul / ol / callout / links / table), so the rendering lives here once.
import React from 'react';
import { Info, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

export const CALLOUT = {
  info: { wrap: 'bg-blue-50 border-blue-200', icon: Info, iconCls: 'text-blue-600', title: 'text-blue-900', body: 'text-blue-800' },
  warning: { wrap: 'bg-amber-50 border-amber-200', icon: AlertTriangle, iconCls: 'text-amber-600', title: 'text-amber-900', body: 'text-amber-800' },
  success: { wrap: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2, iconCls: 'text-emerald-600', title: 'text-emerald-900', body: 'text-emerald-800' },
};

// Minimal inline parser: turns **bold** segments into <strong>.
export const renderInline = (text) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) {
      return <strong key={i} className="font-semibold text-gray-900">{seg.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
};

export const listStyleClass = (style) => {
  if (style === 'roman') return 'list-[lower-roman]';
  if (style === 'alpha') return 'list-[lower-alpha]';
  return 'list-decimal';
};

export const Block = ({ block }) => {
  switch (block.t) {
    case 'p':
      return <p className="text-body leading-relaxed">{renderInline(block.text)}</p>;
    case 'h':
      return <h3 className="text-card-header mt-1">{block.text}</h3>;
    case 'ul':
      return (
        <ul className="list-disc pl-5 space-y-1.5 marker:text-gray-400">
          {block.items.map((it, i) => (
            <li key={i} className="text-body leading-relaxed">{renderInline(it)}</li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className={`${listStyleClass(block.style)} pl-5 space-y-1.5 marker:text-gray-400`}>
          {block.items.map((it, i) => (
            <li key={i} className="text-body leading-relaxed">{renderInline(it)}</li>
          ))}
        </ol>
      );
    case 'callout': {
      const c = CALLOUT[block.tone] || CALLOUT.info;
      const Icon = c.icon;
      return (
        <div className={`flex gap-3 rounded-xl border p-4 ${c.wrap}`}>
          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${c.iconCls}`} />
          <div className="space-y-1 min-w-0">
            {block.title && <p className={`text-sm font-semibold ${c.title}`}>{block.title}</p>}
            <p className={`text-sm leading-relaxed ${c.body}`}>{renderInline(block.text)}</p>
          </div>
        </div>
      );
    }
    case 'links':
      return (
        <div className="flex flex-col gap-2">
          {block.items.map((lnk, i) => (
            <a
              key={i}
              href={lnk.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 rounded w-fit"
            >
              {lnk.label}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ))}
        </div>
      );
    case 'table': {
      const normalizeHeader = (h) => (typeof h === 'string' ? { text: h } : h);
      const headers = (block.headers || []).map(normalizeHeader);
      const colCount = Math.max(
        headers.reduce((n, h) => n + (h.colSpan || 1), 0),
        ...(block.rows || []).map((row) => row.length),
        1,
      );
      return (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[32rem] text-sm border-collapse">
            {block.caption && (
              <caption className="text-left text-xs font-medium text-gray-500 mb-2 px-1">{block.caption}</caption>
            )}
            {headers.length > 0 && (
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  {headers.map((h, i) => (
                    <th
                      key={i}
                      colSpan={h.colSpan || 1}
                      className="py-2.5 px-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                    >
                      {h.text}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody className="divide-y divide-gray-100">
              {(block.rows || []).map((row, ri) => (
                <tr key={ri} className="align-top">
                  {Array.from({ length: colCount }).map((_, ci) => (
                    <td key={ci} className="py-2.5 px-3 text-body leading-relaxed text-gray-800">
                      {row[ci] ? renderInline(row[ci]) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    default:
      return null;
  }
};

export default Block;
